import { filetypeextension, filetypemime } from "magic-bytes.js";
import type { UploadedFile } from "../../../core/schema/UploadedFile.js";
import { slugify } from "../../../core/utils/slugify.js";
import { query } from "./graphql.js";
import getSignedUploadUrl from "./queries/getSignedUploadUrl.graphql";

interface PreparedFile {
	blob: Blob;
	key: string;
	mimeType: string;
	name: string;
}

async function prepareFile(folder: string, blob: File | Blob): Promise<PreparedFile> {
	const fileName = blob instanceof File ? blob.name : "file";
	const fileNameSplitByDot = fileName.split(".");
	const name = fileNameSplitByDot.slice(0, -1).join(".") || "file";
	const extension = fileName !== "file" ? fileNameSplitByDot.pop() : undefined;
	const arrayBuffer = await blob.arrayBuffer();
	const bytes = new Uint8Array(arrayBuffer);

	const detectedFileMimeType = filetypemime(bytes).pop();
	const detectedFileExtension = filetypeextension(bytes)
		.filter((ext) => ext !== "apng")
		.pop();

	const mimeType = (blob.type || detectedFileMimeType) ?? "unknown";
	const fileExtension = (extension ?? detectedFileExtension ?? "").toLowerCase();

	const slugifiedName = slugify(name);
	const key = `${folder}/${slugifiedName}-${crypto.randomUUID()}.${fileExtension}`;

	return {
		key,
		name,
		blob,
		mimeType,
	};
}

async function putObject(presignedUrl: string, preparedFile: PreparedFile): Promise<UploadedFile> {
	const response = await fetch(presignedUrl, {
		method: "PUT",
		body: preparedFile.blob,
	});
	if (!response.ok) {
		throw new Error(`Failed to upload file: ${response.statusText}`);
	}
	return {
		path: preparedFile.key,
		size: preparedFile.blob.size,
		title: preparedFile.name,
		type: preparedFile.mimeType,
	};
}

export async function putObjectToUpload(
	graphQlEndpoint: string,
	blob: File | Blob,
	folder: string,
): Promise<UploadedFile> {
	const preparedFile = await prepareFile(folder, blob);
	const signedUrl = await query<string, { key: string; contentType: string }>(graphQlEndpoint, getSignedUploadUrl, {
		key: preparedFile.key,
		contentType: preparedFile.mimeType,
	});
	return await putObject(signedUrl, preparedFile);
}
